import { emailInputRule, linkInputRule, makeUrl, markAttributesForHref } from 'common/Prose/utils/link'
import { pick } from 'lodash'
import { COUNTER_STYLE, nodes } from 'prose-core'
import { ExtractStrongNodeSpecAttributes } from 'prose-core'
import { InputRule, inputRules } from 'prosemirror-inputrules'
import { Fragment, Node, Schema } from 'prosemirror-model'
import { TextSelection } from 'prosemirror-state'
import { findClosestCodeBlock } from 'proseShared/utils/findClosestCodeBlock'
import { ourUuid } from 'utils' // NOTE: insert uuid so we don't append another tx in the block id plugin and can undo the input rule
import { COMMON_TLDS } from 'utils/constants'
import { isRomanNumeral, romanToDecimal } from 'utils/math'
import { DEFAULT_HIGHLIGHT_COLOR, DEFAULT_QUOTE_COLOR } from '../constants/colorOptions'
import { addMarkdownInputRule } from '../utils/marks'
import { FloatingMenuAnchorPluginKey } from './FloatingMenuAnchorPlugin/FloatingMenuAnchorPlugin'

// Ideally these regexes would use lookbehinds, but WebKit still doesn't have this feature.
// As a workaround we match a character set or start of line: (^|[\s_:=~]). This capture group
// is re-inserted by addMarkdownInputRule. The second capture group is the text content.
// The general pattern for these regexes is (group1)**(group2)** and is transformed to
// (group1)<strong>group2</strong>

function markdownItalicInputRule(schema: Schema) {
  const regex = /(^|[\s_:=~])\*((?=\S)[^*]*(?=\S)[^*])\*$/
  const mark = schema.marks.em.create()
  return addMarkdownInputRule(regex, mark)
}

function markdownBoldInputRule1(schema: Schema) {
  const regex = /(^|[\s*_:=~])\*\*((?=\S)[^*]*(?=\S)[^*])\*\*$/
  const mark = schema.marks.strong.create()
  return addMarkdownInputRule(regex, mark)
}

function markdownBoldInputRule2(schema: Schema) {
  const regex = /(^|[\s*_:=~])__((?!_*$)(?=\S).*?(?=\S).)__$/
  const mark = schema.marks.strong.create()
  return addMarkdownInputRule(regex, mark)
}

function markdownStrikethroughInputRule1(schema: Schema) {
  const regex = /(^|[\s*_:=])~((?=\S)[^~]*?(?=\S)[^~])~$/
  const mark = schema.marks.strike.create()
  return addMarkdownInputRule(regex, mark)
}

function markdownStrikethroughInputRule2(schema: Schema) {
  const regex = /(^|[\s*_:=~])~~((?=\S)[^~]*?(?=\S)[^~])~~$/
  const mark = schema.marks.strike.create()
  return addMarkdownInputRule(regex, mark)
}

function markdownInlineCodeInputRule(schema: Schema) {
  const regex = /(^|[\s*_:=~])`((?=\S).*?(?=\S).)`$/
  const mark = schema.marks.code.create()
  return addMarkdownInputRule(regex, mark)
}

function markdownHighlightInputRule1(schema: Schema) {
  const regex = /(^|[\s*_:=~])::((?=\S).*?(?=\S).)::$/
  const mark = schema.marks.highlighted.create({ color: DEFAULT_HIGHLIGHT_COLOR })
  return addMarkdownInputRule(regex, mark)
}

function markdownHighlightInputRule2(schema: Schema) {
  const regex = /(^|[\s*_:=~])==((?=\S).*?(?=\S).)==$/
  const mark = schema.marks.highlighted.create({ color: DEFAULT_HIGHLIGHT_COLOR })
  return addMarkdownInputRule(regex, mark)
}

function markdownLinkInputRule(schema: Schema) {
  const regex = new RegExp(`([^!]|^)\\[(.+)\\]\\(([\\w:/\\.-]+\\.(?:${COMMON_TLDS.join('|')})(?:/.*)?)\\) ?$`)
  const markFunction = (match: Array<string>) => schema.marks.link.create(markAttributesForHref(makeUrl(match[3])))
  return addMarkdownInputRule(regex, markFunction)
}

function markdownImageInputRule(schema: Schema) {
  const regex = /()!\[(.+)\]\((https?:\/\/.*)\) ?$/
  return new InputRule(regex, (state, match, start, end) => {
    const node = schema.nodes.image.create({ blockId: ourUuid(), description: match[2], src: match[3] })
    const tr = state.tr
    tr.replaceWith(start, end, node)
    return tr
  })
}

function markdownBlockquoteInputRule(schema: Schema) {
  return replaceMatchInputRule(
    /^>\s$/,
    ([match], inlineNodes) =>
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      schema.nodes.box.create(
        {
          quoteColor: DEFAULT_QUOTE_COLOR,
        },
        schema.nodes.paragraph.create({ blockId: ourUuid() }, inlineNodes.cut(match.length - 1)),
      )!,
  )
}

function markdownBlockCodeInputRule(schema: Schema) {
  const regex = /```$/
  return new InputRule(regex, (state, match, start, end) => {
    const $start = state.doc.resolve(start)
    let codeBlockAttrs: Partial<ExtractStrongNodeSpecAttributes<typeof nodes.code_block>> = {
      blockId: ourUuid(),
      lineNumbers: false,
    }
    const closestCodeBlockInfo = findClosestCodeBlock(state.doc, start)
    if (closestCodeBlockInfo !== null) {
      const [, codeBlockNode] = closestCodeBlockInfo
      Object.assign(codeBlockAttrs, pick(codeBlockNode.attrs, ['mime', 'theme', 'lineNumbers']))
    }

    const node = schema.nodes.code_block.create(
      codeBlockAttrs,
      $start.node().content.cut($start.parentOffset + match.length + 1),
    )
    const { tr } = state
    const isLineStart = start === $start.start()
    tr.replaceWith(isLineStart ? $start.before() : start, $start.end(), node)
    tr.setSelection(TextSelection.near(tr.doc.resolve(start + (isLineStart ? 0 : 1))))
    return tr
  })
}

function replaceMatchInputRule(
  regExp: RegExp,
  nodeFunc: (_match: Array<string>, inlineNodes: Fragment, existingNode: Node) => Node,
) {
  return new InputRule(regExp, (state, match, start, end) => {
    const $start = state.doc.resolve(start)
    const $before = state.doc.resolve($start.before())

    if ($before.node().type === state.schema.nodes.list_item) return null

    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const inlineNodes = Fragment.from($before.nodeAfter!.content.content!.filter((node) => node.isInline))
    // TODO: Fix this the next time the file is edited.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const node = nodeFunc(match, inlineNodes, $before.nodeAfter!)
    const tr = state.tr
    tr.replaceWith($before.pos, $start.after(), node)
    tr.setSelection(TextSelection.near(tr.doc.resolve($start.pos)))

    return tr
  })
}

const rules = (schema: Schema, isMobile: boolean) => {
  let ruleList: Array<InputRule> = []
  ruleList.push(
    ...[
      new InputRule(/->$/, '→'),
      new InputRule(/<-$/, '←'),
      new InputRule(/(^|\s)\/(\w*\s{0,2}\w*)$/, (state, match, start, end) => {
        const { tr, doc } = state
        if (end !== tr.selection.to) {
          console.warn({ end, selEnd: tr.selection.to }) // eslint-disable-line no-console
        }
        const rootNodePos = doc.resolve(start).before(1)
        const matchedText = match[2]

        if (match) {
          let matchTerminatorCharacter: string
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          if ((matchTerminatorCharacter = /[-] | \s{1,}/.exec(matchedText)?.[0]!)) {
            tr.insertText(matchTerminatorCharacter)
            tr.setMeta(FloatingMenuAnchorPluginKey, 'clear')
          } else {
            tr.insertText(matchedText ? matchedText.substr(-1) : '/')
            tr.setMeta(FloatingMenuAnchorPluginKey, { slashPos: start, rootPos: rootNodePos, matchedText: matchedText })
          }
          return tr
        }
        return null
      }),
      // Insert ordered list
      // will match 1. 2. 3. ... 99. 100. etc
      // will not match 0. 01. 001. etc
      // hard cap at 6 digits (otherwise the css counter will overflow)
      replaceMatchInputRule(
        /^([0-9]{1,6}|[a-zA-Z])(\.|\))\s$/,
        ([match, num, separator], inlineNodes, existingNode) => {
          let listStartOffset: number = 1
          let counterStyles = [COUNTER_STYLE.DECIMAL, COUNTER_STYLE.LOWER_ALPHA, COUNTER_STYLE.LOWER_ROMAN]
          if (/[0-9]+/.test(num)) {
            listStartOffset = parseInt(num, 10) || 1 // convert 0. to 1.
          }
          if (/[a-zA-Z]+/.test(num) && separator !== '.') {
            separator = '.' // convert a) to a.
          }
          if (isRomanNumeral(num)) {
            listStartOffset = romanToDecimal(num) || 1
            counterStyles = [COUNTER_STYLE.UPPER_ROMAN, COUNTER_STYLE.UPPER_LATIN, COUNTER_STYLE.DECIMAL]
          } else {
            if (/[a-z]+/.test(num)) {
              counterStyles = [COUNTER_STYLE.UPPER_LATIN, COUNTER_STYLE.LOWER_ALPHA, COUNTER_STYLE.LOWER_ROMAN]
              listStartOffset = num.split('').reduce((acc, char, idx) => {
                acc += (char.charCodeAt(0) - 96) * Math.pow(26, num.length - idx - 1)

                return acc
              }, 0)
            }
            if (/[A-Z]+/.test(num)) {
              listStartOffset = num.charCodeAt(0) - 64 // convert A. to 1.
              counterStyles = [COUNTER_STYLE.UPPER_LATIN, COUNTER_STYLE.LOWER_ALPHA, COUNTER_STYLE.LOWER_ROMAN]
            }
          }

          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          return schema.nodes.ordered_list.create(
            {
              blockId: ourUuid(),
              counterSeparator: separator,
              counterStyles,
              flattenMarker: true,
              listStartOffset,
            },
            schema.nodes.list_item.create(
              { depth: existingNode.attrs.depth || 0 },
              schema.nodes.paragraph.create({}, inlineNodes.cut(match.length - 1)),
            ),
          )!
        },
      ),
      // Insert heading
      replaceMatchInputRule(
        /^(#{1,4})\s$/,
        ([match, hashMatch], inlineNodes, existingNode) =>
          schema.nodes.heading.create(
            {
              alignment: existingNode.attrs.alignment,
              blockId: ourUuid(),
              depth: existingNode.attrs.depth || 0,
              level: hashMatch.length as 1 | 2 | 3 | 4,
            },
            inlineNodes.cut(match.length - 1),
          ) as Node,
      ),
      // Insert checklist
      replaceMatchInputRule(/^(\[ ?[xX]? ?\] )$/, (match, inlineNodes, existingNode) =>
        schema.nodes.check_list.create(
          {
            blockId: ourUuid(),
          },
          schema.nodes.list_item.create(
            { checked: /[xX]/.test(match[1]), depth: existingNode.attrs.depth || 0 },
            schema.nodes.paragraph.create({}, inlineNodes.cut(match[0].length - 1)),
          ),
        ),
      ),
      // Insert unordered list
      replaceMatchInputRule(
        /^[*-]\s$/,
        ([match], inlineNodes, existingNode) =>
          // TODO: Fix this the next time the file is edited.
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          schema.nodes.unordered_list.create(
            {
              blockId: ourUuid(),
            },
            schema.nodes.list_item.create(
              { depth: existingNode.attrs.depth || 0 },
              schema.nodes.paragraph.create({}, inlineNodes.cut(match.length - 1)),
            ),
          )!,
      ),
      // Insert line divider
      replaceMatchInputRule(
        /^---$/,
        ([match], inlineNodes, _existingNode) =>
          schema.nodes.line_divider.create(
            {
              blockId: ourUuid(),
            },
            inlineNodes.cut(match.length - 1),
          ) as Node,
      ),
    ],
  )
  // Most inline markdown operators are disabled on mobile due to a bug (and needs more QA in general):
  // The cursor incorrectly gets active marks after applying markdown styles. e.g. after typing *test* the cursor should not have an active italic mark.
  if (!isMobile) {
    ruleList.push(
      ...[
        markdownItalicInputRule(schema),
        markdownBoldInputRule1(schema),
        markdownBoldInputRule2(schema),
        markdownStrikethroughInputRule1(schema),
        markdownStrikethroughInputRule2(schema),
      ],
    )
  }
  ruleList.push(...[markdownBlockCodeInputRule(schema), markdownInlineCodeInputRule(schema)])
  if (!isMobile) {
    ruleList.push(
      ...[
        markdownHighlightInputRule1(schema),
        markdownHighlightInputRule2(schema),
        markdownLinkInputRule(schema),
        markdownImageInputRule(schema),
      ],
    )
  }
  ruleList.push(...[markdownBlockquoteInputRule(schema), emailInputRule(schema), linkInputRule(schema)])
  return ruleList
}

/**
 * Disabled (for now) due to internal request
 */
// if (featureFlag) {
//   rules.push(
//     replaceMatchInputRule(
//       /^\+\s$/,
//       ([match], inlineNodes, existingNode) =>
//         schema.nodes.toggle_list.create(
//           {
//             blockId: ourUuid(),
//           },
//           [
//             schema.nodes.summary.create({}, schema.nodes.paragraph.create({}, inlineNodes.cut(match.length - 1))),
//             schema.nodes.paragraph.create({}),
//           ],
//         )!,
//     ),
//   )
// }

export const InputRules = (schema: Schema, isMobile: boolean) => inputRules({ rules: rules(schema, isMobile) })
